import { useEffect, useMemo, useState } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, X, FolderHeart, ChevronDown, Pencil, Trash2, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CATEGORIES, CATEGORY_STYLES } from "@/lib/session";
import { MinistryLibraryPanel } from "@/components/MinistryLibraryPanel";

const MINISTRY_TAB = "Ministry";
const PILLAR_TABS = [...CATEGORIES, MINISTRY_TAB];
const MINISTRY_STYLE = {
  badge: "bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800",
  header: "bg-rose-900 text-rose-50",
  border: "border-rose-900",
};

function groupBySubcategory(items) {
  const groups = {};
  for (const item of items) {
    const key = item.subcategory || "General";
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }
  return Object.entries(groups);
}

function PrayerButton({ item, onSelect, testId }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={() => onSelect(item)}
          className="w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-[var(--brand-gold)]/20 transition-colors"
          data-testid={testId}
        >
          {item.title}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs whitespace-normal">
        {item.body ? item.body.slice(0, 140) + (item.body.length > 140 ? "..." : "") : "No preview available"}
      </TooltipContent>
    </Tooltip>
  );
}

function AddCustomPrayerForm({ category, folders, onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [duration, setDuration] = useState(5);
  const [folderId, setFolderId] = useState(folders[0]?.id || "__new__");
  const [newFolderName, setNewFolderName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFolderId(folders[0]?.id || "__new__");
  }, [category]);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      let targetFolderId = folderId;
      let updatedFolder;
      if (folderId === "__new__") {
        if (!newFolderName.trim()) {
          setSaving(false);
          return;
        }
        const { data } = await api.post("/saved-folders", { category, name: newFolderName.trim() });
        targetFolderId = data.id;
        updatedFolder = data;
      }
      const { data: prayer } = await api.post(`/saved-folders/${targetFolderId}/prayers`, {
        title: title.trim(),
        body,
        duration_minutes: duration,
      });
      onCreated(targetFolderId, prayer, updatedFolder);
      setTitle("");
      setBody("");
      setDuration(5);
      setNewFolderName("");
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-[var(--brand-gold-text)] dark:text-amber-300 hover:underline px-2 py-2.5 border-t border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] mt-1"
          data-testid={`add-custom-prayer-trigger-${category.replace('/', '-')}`}
        >
          <Plus className="h-3.5 w-3.5" /> New {category.toLowerCase()}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-2" data-testid={`add-custom-prayer-popover-${category.replace('/', '-')}`}>
        <p className="text-sm font-semibold">Save a new {category.toLowerCase()}</p>
        <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-prayer-title-input" />
        <Textarea placeholder="Prayer text..." rows={3} value={body} onChange={(e) => setBody(e.target.value)} data-testid="new-prayer-body-input" />
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground shrink-0">Minutes</label>
          <Input type="number" min={1} value={duration} onChange={(e) => setDuration(Number(e.target.value) || 1)} className="h-8" data-testid="new-prayer-duration-input" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Save into folder</label>
          <Select value={folderId} onValueChange={setFolderId}>
            <SelectTrigger className="w-full mt-1 h-8 text-sm" data-testid="new-prayer-folder-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {folders.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
              <SelectItem value="__new__">+ Create new folder</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {folderId === "__new__" && (
          <Input placeholder="New folder name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} data-testid="new-folder-name-input" />
        )}
        <Button onClick={submit} disabled={saving || !title.trim()} className="w-full bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="new-prayer-submit-button">
          {saving ? "Saving..." : "Save & add to session"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export function InlineCategoryPicker({ onSelect, onClose }) {
  const [items, setItems] = useState([]);
  const [savedFolders, setSavedFolders] = useState([]);
  const [search, setSearch] = useState("");
  const [activePillar, setActivePillar] = useState(CATEGORIES[0]);

  useEffect(() => {
    api.get("/library").then(({ data }) => setItems(data)).catch(() => setItems([]));
    api.get("/saved-folders").then(({ data }) => setSavedFolders(data)).catch(() => setSavedFolders([]));
  }, []);

  const activeItems = useMemo(
    () => items.filter((i) => i.category === activePillar && i.title.toLowerCase().includes(search.toLowerCase())),
    [items, activePillar, search]
  );
  const activeGroups = useMemo(() => groupBySubcategory(activeItems), [activeItems]);
  const activeFolders = useMemo(() => savedFolders.filter((f) => f.category === activePillar), [savedFolders, activePillar]);
  const isMinistryTab = activePillar === MINISTRY_TAB;
  const activeStyle = isMinistryTab ? MINISTRY_STYLE : CATEGORY_STYLES[activePillar];
  const isSearching = search.trim().length > 0;
  const matchingFolderPrayers = useMemo(() => {
    if (!isSearching) return [];
    return activeFolders.flatMap((f) =>
      f.prayers.filter((p) => p.title.toLowerCase().includes(search.toLowerCase())).map((p) => ({ ...p, _folderName: f.name }))
    );
  }, [activeFolders, search, isSearching]);

  const handleFolderCreated = (folderId, prayer, newFolder) => {
    setSavedFolders((prev) => {
      if (newFolder) return [...prev, { ...newFolder, prayers: [prayer] }];
      return prev.map((f) => (f.id === folderId ? { ...f, prayers: [...f.prayers, prayer] } : f));
    });
    onSelect(prayer);
  };

  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editFolderName, setEditFolderName] = useState("");

  const startRename = (folder) => {
    setEditingFolderId(folder.id);
    setEditFolderName(folder.name);
  };

  const submitRename = async () => {
    if (!editFolderName.trim()) return;
    const { data } = await api.put(`/saved-folders/${editingFolderId}`, { name: editFolderName.trim() });
    setSavedFolders((prev) => prev.map((f) => (f.id === editingFolderId ? { ...f, name: data.name } : f)));
    setEditingFolderId(null);
  };

  const handleDeleteFolder = async (folderId, folderName) => {
    if (!window.confirm(`Delete folder "${folderName}" and all prayers saved in it?`)) return;
    await api.delete(`/saved-folders/${folderId}`);
    setSavedFolders((prev) => prev.filter((f) => f.id !== folderId));
    toast.success("Folder deleted");
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg overflow-hidden mb-5 animate-in fade-in slide-in-from-top-2" data-testid="inline-category-picker">
        <div className="flex items-center justify-between px-4 py-3 bg-[var(--brand-navy)] text-amber-50">
          <p className="font-display text-lg font-semibold">Choose which prayer, devotional, chant, song, or activity to add</p>
          <button onClick={onClose} className="text-amber-100 hover:text-white" data-testid="inline-picker-close-button">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-3 bg-[#F5F3EE] dark:bg-[var(--brand-bg-dark)]">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder={`Search ${activePillar.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white dark:bg-[var(--brand-card-dark)]" data-testid="inline-picker-search-input" />
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3" data-testid="pillar-tabs">
            {PILLAR_TABS.map((cat) => {
              const style = cat === MINISTRY_TAB ? MINISTRY_STYLE : CATEGORY_STYLES[cat];
              const isActive = cat === activePillar;
              return (
                <button
                  key={cat}
                  onClick={() => setActivePillar(cat)}
                  className={`rounded-lg font-display text-base sm:text-lg font-semibold py-3 px-2 text-center transition-all ${
                    isActive ? `${style.header} shadow-md scale-[1.02]` : `${style.badge} border hover:shadow-sm hover:-translate-y-px`
                  }`}
                  data-testid={`pillar-tab-${cat.replace('/', '-')}`}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div className={`rounded-lg border-2 ${activeStyle.border} bg-white dark:bg-[var(--brand-card-dark)] overflow-hidden`} data-testid="pillar-content-panel">
            {isMinistryTab ? (
              <div className="max-h-96 overflow-y-auto p-2">
                <MinistryLibraryPanel onSelect={onSelect} search={search} />
              </div>
            ) : (
            <div className="max-h-96 overflow-y-auto p-2">
              {isSearching ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-0.5">
                  {activeItems.length === 0 && matchingFolderPrayers.length === 0 && (
                    <p className="col-span-full text-sm text-muted-foreground text-center py-6">No {activePillar.toLowerCase()} match your search.</p>
                  )}
                  {activeItems.map((item) => (
                    <PrayerButton key={item.id} item={item} onSelect={onSelect} testId={`category-item-${item.id}`} />
                  ))}
                  {matchingFolderPrayers.map((item) => (
                    <div key={item.id} className="flex items-center gap-1.5">
                      <PrayerButton item={item} onSelect={onSelect} testId={`saved-prayer-item-${item.id}`} />
                      <span className="text-[10px] text-muted-foreground shrink-0 pr-2 italic">{item._folderName}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {activeGroups.length === 0 && activeFolders.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-6">Nothing here yet.</p>
                  )}
                  <Accordion type="single" collapsible>
                    {activeGroups.map(([subcat, subItems]) => (
                      <AccordionItem key={subcat} value={subcat} className="border-b border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] px-2">
                        <AccordionTrigger className="text-base py-2.5" data-testid={`subcategory-trigger-${activePillar.replace('/', '-')}-${subcat.replace(/\s+/g, '-')}`}>
                          {subcat}
                        </AccordionTrigger>
                        <AccordionContent className="pb-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-0.5">
                          {subItems.map((item) => (
                            <PrayerButton key={item.id} item={item} onSelect={onSelect} testId={`category-item-${item.id}`} />
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                    {activeFolders.map((folder) => (
                      <AccordionItem key={folder.id} value={`folder-${folder.id}`} className="border-b border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] px-2">
                        {editingFolderId === folder.id ? (
                          <div className="flex items-center gap-2 py-2.5">
                            <FolderHeart className="h-4 w-4 text-[var(--brand-gold-text)] dark:text-amber-300 shrink-0" />
                            <Input
                              autoFocus
                              value={editFolderName}
                              onChange={(e) => setEditFolderName(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && submitRename()}
                              className="h-7 text-sm"
                              data-testid={`folder-rename-input-${folder.id}`}
                            />
                            <button onClick={submitRename} className="text-emerald-600 hover:text-emerald-700 shrink-0" data-testid={`folder-rename-save-${folder.id}`}>
                              <Check className="h-4 w-4" />
                            </button>
                            <button onClick={() => setEditingFolderId(null)} className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`folder-rename-cancel-${folder.id}`}>
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <AccordionPrimitive.Header className="flex items-center">
                            <AccordionPrimitive.Trigger
                              className="flex-1 flex items-center justify-between py-2.5 text-base italic text-left hover:underline [&[data-state=open]>svg]:rotate-180"
                              data-testid={`saved-folder-trigger-${folder.id}`}
                            >
                              <span className="flex items-center gap-1.5"><FolderHeart className="h-4 w-4 text-[var(--brand-gold-text)] dark:text-amber-300" /> {folder.name}</span>
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                            </AccordionPrimitive.Trigger>
                            <div className="flex items-center gap-2 pl-2 shrink-0">
                              <button onClick={(e) => { e.stopPropagation(); startRename(folder); }} className="text-muted-foreground hover:text-foreground" data-testid={`folder-rename-button-${folder.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id, folder.name); }} className="text-muted-foreground hover:text-[var(--brand-crimson)]" data-testid={`folder-delete-button-${folder.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </AccordionPrimitive.Header>
                        )}
                        <AccordionContent className="pb-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-0.5">
                          {folder.prayers.length === 0 && <p className="col-span-full text-xs text-muted-foreground px-2">No saved prayers yet</p>}
                          {folder.prayers.map((item) => (
                            <PrayerButton key={item.id} item={item} onSelect={onSelect} testId={`saved-prayer-item-${item.id}`} />
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </>
              )}
            </div>
            )}
            {!isMinistryTab && <AddCustomPrayerForm category={activePillar} folders={activeFolders} onCreated={handleFolderCreated} />}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
